/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchAccounts, connectMT5, connectIB, getMT5Account, shutdownInstance, restartInstance, startOnboardingAndWait, fetchRiskSettings } from "../api/api";
import EditAccountModal from "./EditAccountModal";
import CreateAccountModal from "./CreateAccountModal";
import VncModal from "../components/VncModal";
import { onboardStart } from "../api/ctraderApi";

// cTrader selection is handled on /select-ctrader-account route

const FIRST_ACCOUNT_SKIP_KEY = "paxia:first-account-onboarding-skipped";

const FIRST_ACCOUNT_PLATFORMS = [
  {
    value: "MT5",
    label: "MetaTrader 5",
    badge: "Fastest setup",
    summary: "Best when you already have your MT5 login details.",
    requirements: ["Account number", "Password", "Server"],
  },
  {
    value: "cTrader",
    label: "cTrader",
    badge: "OAuth flow",
    summary: "Best when your broker already uses cTrader.",
    requirements: ["Browser sign-in", "Linked account"],
  },
  {
    value: "InteractiveBrokers",
    label: "Interactive Brokers",
    badge: "Paper or live",
    summary: "Use your IB credentials for either paper or live trading.",
    requirements: ["Username", "Password", "Account ID", "Paper or live"],
  },
];

const Accounts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPlatform, setCreatePlatform] = useState("MT5");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [connectingAccountId, setConnectingAccountId] = useState(null);
  const [vncDetails, setVncDetails] = useState(null);
  const [vncAccountId, setVncAccountId] = useState(null);
  const [vncLoading, setVncLoading] = useState(false);
  // cTrader selection UI moved to dedicated page
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedAccountTab, setSelectedAccountTab] = useState("edit");
  const [riskStatus, setRiskStatus] = useState({}); // accountId -> boolean (true = configured)

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const userAccounts = await fetchAccounts();
      setAccounts(userAccounts);
      if (userAccounts.length > 0) {
        try {
          sessionStorage.removeItem(FIRST_ACCOUNT_SKIP_KEY);
        } catch {
          void 0;
        }
      }
      // Fetch risk settings for all accounts in parallel to detect unconfigured ones
      const results = await Promise.allSettled(
        userAccounts.map((acc) => fetchRiskSettings(acc.id))
      );
      const statusMap = {};
      results.forEach((result, idx) => {
        const accId = userAccounts[idx].id;
        if (result.status === "fulfilled") {
          const data = result.value;
          // max_daily_loss is the only required field with no default
          // Only mark as unconfigured if the API confirmed it (200 with null)
          statusMap[accId] = data?.max_daily_loss != null;
        } else {
          // Request failed — don't show a false warning; leave status unknown (null)
          statusMap[accId] = null;
        }
      });
      setRiskStatus(statusMap);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  const openEditModal = (account, tab = "edit") => {
    setSelectedAccountTab(tab);
    setSelectedAccount(account);
  };

  useEffect(() => {
    const requestedAccountId = location.state?.openAccountId;
    const requestedTab = location.state?.openTab || "edit";

    if (!requestedAccountId || accounts.length === 0) {
      return;
    }

    const targetAccount = accounts.find((account) => String(account.id) === String(requestedAccountId));

    if (targetAccount) {
      openEditModal(targetAccount, requestedTab);
    }

    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [accounts, location.pathname, location.search, location.state, navigate]);

  const handleConnect = async (account) => {
    console.log("🔹 Debug: Account object received in handleConnect:", account);

    if (account.platform === "MT5") {
      let mt5AccountDetails;
      try {
        mt5AccountDetails = await getMT5Account(account.id);
        console.log("🔹 Debug: MT5 account details received:", mt5AccountDetails);
      } catch (err) {
        console.error("Error fetching MT5 account details:", err);
        alert("Failed to retrieve MT5 account details.");
        return;
      }

      if (!mt5AccountDetails || !mt5AccountDetails.account_number || !mt5AccountDetails.password || !mt5AccountDetails.broker_server) {
        console.log("🔹 Debug: Missing fields - account_number:", mt5AccountDetails?.account_number, "password:", mt5AccountDetails?.password ? "[PRESENT]" : "[MISSING]", "broker_server:", mt5AccountDetails?.broker_server);
        alert("MT5 account details are incomplete.");
        return;
      }

      setConnectingAccountId(account.id);
      try {
        await connectMT5(account.id, {
          account_number: mt5AccountDetails.account_number,
          password: mt5AccountDetails.password,
          broker_server: mt5AccountDetails.broker_server,
        });
        loadAccounts();
      } catch (err) {
        console.error("Error connecting to MT5:", err);
        alert("Failed to connect to MT5.");
      } finally {
        setConnectingAccountId(null);
      }
    } else if (account.platform === "InteractiveBrokers") {
      setConnectingAccountId(account.id);
      try {
        await connectIB(account.id);
        loadAccounts();
      } catch (err) {
        console.error("Error connecting to IB:", err);
        alert("Failed to connect to Interactive Brokers.");
      } finally {
        setConnectingAccountId(null);
      }
    } else if (account.platform === "cTrader") {
      try {
        console.log(`🔹 Requesting cTrader login for account: ${account.id}`);
        // Persist the internal account id so we can finish onboarding after OAuth
        const idStr = String(account.id);
    localStorage.setItem("ctrader_pending_internal_account_id", idStr);
  try { sessionStorage.setItem("ctrader_pending_internal_account_id", idStr); } catch { void 0; }
        try {
          // Also set a short-lived cookie (15 minutes) for cross-tab/callback robustness
          document.cookie = `ctrader_pending_internal_account_id=${encodeURIComponent(idStr)}; Max-Age=900; Path=/; SameSite=Lax`;
        } catch { void 0; }

        // Open popup synchronously to avoid blockers
        const features = "width=520,height=720,noopener,noreferrer";
        let popup = null;
        try {
          popup = window.open("about:blank", "ctraderAuth", features);
        } catch { popup = null; }

        // Inform app that auth has started so it can show the modal
        try {
          window.postMessage({ type: "CTRADER_AUTH_STARTED" }, window.location.origin);
        } catch { void 0; }

        // Start onboarding via backend; it returns a redirect_url to cTrader auth
        const data = await onboardStart(account.id);
        const redirectUrl = data?.redirect_url || data?.redirect || data?.url;
        if (redirectUrl) {
          console.log("✅ Redirect URL received:", redirectUrl);
          if (popup && !popup.closed) {
            try { popup.location.href = redirectUrl; }
            catch { window.location.href = redirectUrl; }
          } else {
            // Fallback to full-page redirect if popup blocked
            window.location.href = redirectUrl;
          }
        } else {
          if (popup && !popup.closed) try { popup.close(); } catch { void 0; }
          alert("Failed to generate cTrader login URL.");
        }
      } catch (error) {
        console.error("❌ Error generating cTrader login URL:", error);
      }
    }
  };

  const handleOpenVnc = async (account) => {
    if (account.platform !== "MT5") return;
    
    setVncLoading(true);
    try {
      // Reuse onboarding endpoint to start VNC session
      const response = await startOnboardingAndWait({ internal_account_id: account.id });
      if (response?.vnc_details) {
        setVncDetails(response.vnc_details);
        setVncAccountId(account.id);
      } else {
        alert("Failed to start VNC session.");
      }
    } catch (err) {
      console.error("Error starting VNC session:", err);
      alert("Failed to start VNC session.");
    } finally {
      setVncLoading(false);
    }
  };

  const closeVncModal = () => {
    setVncDetails(null);
    setVncAccountId(null);
  };
  // Accounts selection happens on /select-ctrader-account route

  const openCreateModal = (platform = "MT5") => {
    setCreatePlatform(platform);
    setShowCreateModal(true);
  };

  const handleSkipOnboarding = () => {
    try {
      sessionStorage.setItem(FIRST_ACCOUNT_SKIP_KEY, "1");
    } catch {
      void 0;
    }
    navigate("/dashboard");
  };

  const hasAccounts = accounts.length > 0;

  return (
    <div className="max-w-4xl mx-auto mt-4 sm:mt-10 p-4 sm:p-6 bg-backgroundSurface shadow-md rounded-xl text-textPrimary">
      <h2 className="text-xl sm:text-2xl font-semibold text-center text-primary">Manage Accounts</h2>
      {!hasAccounts ? (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-5">
          <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-[radial-gradient(circle_at_top_left,_rgba(24,119,242,0.24),_transparent_45%),linear-gradient(135deg,_rgba(15,24,38,0.98),_rgba(17,31,52,0.96))] p-5 sm:p-7 shadow-xl">
            <div className="relative z-10 space-y-5">
              <div className="max-w-2xl space-y-3">
                <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-primary">
                  First-time setup
                </span>
                <h3 className="text-2xl sm:text-3xl font-semibold text-white">Choose your trading platform</h3>
                <p className="max-w-xl text-sm sm:text-base text-slate-200/85">
                  Start with the account you already use. You can add more connections later.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                {FIRST_ACCOUNT_PLATFORMS.map((platform) => (
                  <div
                    key={platform.value}
                    role="button"
                    tabIndex={0}
                    aria-label={`Select ${platform.label}`}
                    onClick={() => openCreateModal(platform.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openCreateModal(platform.value);
                      }
                    }}
                    className="group flex min-h-[220px] cursor-pointer flex-col rounded-2xl border border-white/10 bg-white/6 p-4 text-left shadow-lg backdrop-blur transition duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:bg-white/8 focus:outline-none focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <div>
                      <div className="text-lg font-semibold text-white">{platform.label}</div>
                      <div className="mt-1 text-xs uppercase tracking-[0.18em] text-primary/90">{platform.badge}</div>
                      <p className="mt-4 text-sm leading-6 text-slate-200/82">{platform.summary}</p>
                      <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">You'll need</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {platform.requirements.map((item) => (
                            <span
                              key={item}
                              className="rounded-full border border-white/10 bg-black/10 px-2.5 py-1 text-xs text-slate-200/90"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-6 text-sm font-semibold text-white">
                      <span className="transition group-hover:text-primary group-focus-visible:text-primary">
                        Select {platform.label}
                      </span>
                      <span className="text-primary transition group-hover:translate-x-1 group-focus-visible:translate-x-1">
                        &gt;
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={handleSkipOnboarding}
                  className="text-sm font-medium text-slate-300 transition hover:text-white focus:outline-none focus-visible:text-white"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-4 sm:mt-6">
          {accounts.map((account) => (
            <div key={account.id} className="p-4 sm:p-5 bg-backgroundBody rounded-lg shadow-md text-textPrimary flex flex-col gap-2 min-h-[140px] sm:min-h-[160px]">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-textPrimary truncate">
                  {account.name}
                  {account.is_default && (
                    <span className="ml-2 text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">Default</span>
                  )}
                </h3>
                <p className="text-xs sm:text-sm text-textSecondary mt-0.5">{account.broker} - {account.platform}</p>
              </div>
              {riskStatus[account.id] === false && (
                <div className="flex items-center gap-1.5 text-xs text-warning bg-warning/10 border border-warning/30 rounded px-2 py-1">
                  <span>⚠</span>
                  <span>Risk settings not configured.</span>
                  <button
                    onClick={() => openEditModal(account, "risk")}
                    className="underline text-warning hover:text-warning/80 ml-0.5"
                  >
                    Set up now
                  </button>
                </div>
              )}
              <div className="flex justify-between items-center mt-auto gap-2">
                <div className="flex gap-3">
                  <button onClick={() => openEditModal(account, "edit")} className="text-primary hover:underline text-sm">Edit</button>
                  <button onClick={() => openEditModal(account, "risk")} className="text-primary hover:underline text-sm">Risk Settings</button>
                </div>
                <div className="relative">
                  <button onClick={() => setOpenMenuId(openMenuId === account.id ? null : account.id)} className="btn btn-primary btn-xs sm:btn-sm">
                    Actions
                  </button>
                  {openMenuId === account.id && (
                    <div className="absolute right-0 mt-2 w-40 sm:w-48 bg-backgroundSurface rounded-md shadow-lg z-10">
                      <button
                        onClick={() => {
                          shutdownInstance(account.id);
                          setOpenMenuId(null);
                        }}
                        className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-textPrimary hover:bg-secondary/20"
                      >
                        Shutdown
                      </button>
                      <button
                        onClick={() => {
                          restartInstance(account.id);
                          setOpenMenuId(null);
                        }}
                        className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-textPrimary hover:bg-secondary/20"
                      >
                        Restart
                      </button>
                       <button
                        onClick={() => {
                          handleConnect(account);
                          setOpenMenuId(null);
                        }}
                        disabled={connectingAccountId === account.id}
                        className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-textPrimary hover:bg-secondary/20"
                      >
                        {connectingAccountId === account.id ? 'Connecting...' : 'Connect'}
                      </button>
                      {account.platform === "MT5" && (
                        <button
                          onClick={() => {
                            handleOpenVnc(account);
                            setOpenMenuId(null);
                          }}
                          disabled={vncLoading}
                          className="block w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm text-textPrimary hover:bg-secondary/20 disabled:opacity-50"
                        >
                          {vncLoading ? 'Opening VNC...' : 'Open VNC'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="min-h-[140px] sm:min-h-[160px] flex items-center justify-center bg-backgroundBody hover:bg-secondary/20 rounded-lg shadow-md cursor-pointer" onClick={() => openCreateModal()}>
            <span className="text-3xl sm:text-4xl text-textSecondary">+</span>
          </div>
        </div>
      )}
  {showCreateModal && <CreateAccountModal closeModal={() => setShowCreateModal(false)} refreshAccounts={loadAccounts} initialPlatform={createPlatform} mode={hasAccounts ? "standard" : "onboarding"} />}
  {selectedAccount && <EditAccountModal account={selectedAccount} closeModal={() => setSelectedAccount(null)} refreshAccounts={loadAccounts} initialTab={selectedAccountTab} />}
  {vncLoading && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-backgroundSurface rounded-lg p-6 flex flex-col items-center gap-3 shadow-xl">
        <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="text-textPrimary text-sm font-medium">Starting VNC session...</p>
      </div>
    </div>
  )}
  {vncDetails && vncAccountId && (
    <VncModal 
      vncDetails={vncDetails} 
      internalAccountId={vncAccountId} 
      closeModal={closeVncModal} 
      refreshAccounts={loadAccounts} 
    />
  )}
    </div>
  );
};

export default Accounts;
